import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from '../app.state';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { CaseManagementService } from '../../services/case-management.service';
import { UtilsService } from '../../edge/utils.service';
import { catchError, concatMap, filter, map, mergeMap, share, switchMap, takeUntil } from 'rxjs';
import { concatLatestFrom } from '@ngrx/operators';
import { SharedActions } from '@states/shared/shared.action-types';
import { CaseAttachmentActions } from '@states/case-attachment/case-attachment.actions';
import { caseAttachmentFeature } from '@states/case-attachment/case-attachment.feature';
import { CaseManagementActions } from '@states/case-management/case-management.actions';

@Injectable()
export class CaseAttachmentEffect {
  private readonly store$ = inject<Store<AppState>>(Store);
  private readonly actions$ = inject(Actions);
  private readonly caseManagementService = inject(CaseManagementService);
  private readonly utilService = inject(UtilsService);

  public beforeGetAttachments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaseAttachmentActions.beforeGetAttachments),
      map(({ caseId }) => CaseAttachmentActions.getAttachments({ caseId })),
    ),
  );

  public beforeGetFileRequestAttachments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaseAttachmentActions.beforeGetFileRequestAttachments),
      map(({ caseId }) => CaseAttachmentActions.getFileRequestAttachments({ caseId })),
    ),
  );

  public getAttachmentById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaseAttachmentActions.getAttachmentById),
      switchMap(({ id }) =>
        this.caseManagementService.getAttachmentById(id).pipe(
          map((attachment) => CaseAttachmentActions.getAttachmentByIdSuccess({ attachment })),
          catchError((error) => [
            CaseAttachmentActions.getAttachmentByIdFail(),
            SharedActions.showMessage({ error: this.utilService.errMessage(error) }),
          ]),
        ),
      ),
      share(),
    ),
  );

  public getAttachments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaseAttachmentActions.getAttachments),
      concatLatestFrom(() => [
        this.store$.select(caseAttachmentFeature.selectLatestTs),
        this.store$.select(caseAttachmentFeature.selectIsLastPage),
      ]),
      switchMap(([{ caseId }, latestTs, isLastPage]) => {
        if (!isLastPage) {
          return this.caseManagementService.getAttachments(caseId, latestTs).pipe(
            map((attachments) => CaseAttachmentActions.getAttachmentsSuccess({ attachments })),
            catchError((error) => [
              CaseAttachmentActions.getAttachmentsFail(),
              SharedActions.showMessage({ error: this.utilService.errMessage(error) }),
            ]),
          );
        }

        return [SharedActions.doNothing()];
      }),
      share(),
    ),
  );

  public getFileRequestAttachments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaseAttachmentActions.getFileRequestAttachments),
      concatLatestFrom(() => [
        this.store$.select(caseAttachmentFeature.selectFileRequestLatestTs),
        this.store$.select(caseAttachmentFeature.selectIsFileRequestLastPage),
      ]),
      switchMap(([{ caseId }, latestTs, isLastPage]) => {
        if (!isLastPage) {
          return this.caseManagementService.getFileRequestAttachments(caseId, latestTs).pipe(
            map((attachments) => CaseAttachmentActions.getFileRequestAttachmentsSuccess({ attachments })),
            catchError((error) => [
              CaseAttachmentActions.getFileRequestAttachmentsFail(),
              SharedActions.showMessage({ error: this.utilService.errMessage(error) }),
            ]),
          );
        }

        return [SharedActions.doNothing()];
      }),
      share(),
    ),
  );

  public createAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaseAttachmentActions.createAttachment),
      switchMap(({ data }) =>
        this.caseManagementService.addAttachment(data).pipe(
          mergeMap((attachment) => [
            CaseAttachmentActions.createAttachmentSuccess({ attachment }),
            CaseManagementActions.getCaseFeatureCounters({ id: data.caseId }),
            SharedActions.showMessage({ success: 'Attachment created successfully' }),
          ]),
          catchError((error) => [
            CaseAttachmentActions.createAttachmentFail(),
            SharedActions.showMessage({ error: this.utilService.errMessage(error) }),
          ]),
        ),
      ),
      share(),
    ),
  );

  public updateAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaseAttachmentActions.updateAttachment),
      switchMap(({ id, data }) =>
        this.caseManagementService.updateAttachment(id, data).pipe(
          mergeMap((attachment) => [
            CaseAttachmentActions.updateAttachmentSuccess({ attachment }),
          ]),
          catchError((error) => [
            CaseAttachmentActions.updateAttachmentFail(),
            SharedActions.showMessage({ error: this.utilService.errMessage(error) }),
          ]),
        ),
      ),
      share(),
    ),
  );

  public deleteAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaseAttachmentActions.deleteAttachment),
      concatMap(({ id, caseId }) =>
        this.caseManagementService.deleteAttachment(id, caseId).pipe(
          mergeMap(() => [
            CaseAttachmentActions.deleteAttachmentSuccess({ id }),
            CaseManagementActions.getCaseFeatureCounters({ id: caseId }),
          ]),
          catchError((error) => [
            CaseAttachmentActions.deleteAttachmentFail(),
            SharedActions.showMessage({ error: this.utilService.errMessage(error) }),
          ]),
        ),
      ),
      share(),
    ),
  );

  public uploadAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaseAttachmentActions.uploadAttachment),
      switchMap(({ caseId, filename, file }) => {
        const cancel$ = this.actions$.pipe(
          ofType(CaseAttachmentActions.cancelUploadAttachment),
          filter((cancelAction) => cancelAction.caseId === caseId && cancelAction.filename === filename),
        );

        return this.caseManagementService.generateAttachmentUploadUrl({
          caseId,
          filename,
        }).pipe(
          switchMap(({ url, filename: resolvedFilename, headers }) =>
            this.caseManagementService.uploadAttachmentToSignedUrl({
              url,
              file,
              filename,
              headers,
            }).pipe(
              map(() => resolvedFilename),
            ),
          ),
          takeUntil(
            cancel$,
          ),
          mergeMap((resolvedFilename) => [
            CaseAttachmentActions.uploadAttachmentSuccess({ caseId, filename: resolvedFilename }),
          ]),
          catchError((error) => [
            CaseAttachmentActions.uploadAttachmentFail({ caseId, filename }),
            SharedActions.showMessage({ error: this.utilService.errMessage(error) }),
          ]),
        );
      }),
      share(),
    ),
  );

  public downloadAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaseAttachmentActions.downloadAttachment),
      switchMap(({ id, filename }) =>
        this.caseManagementService.downloadAttachment(id).pipe(
          mergeMap((response) => {
            if (!response.body) {
              return [
                CaseAttachmentActions.downloadAttachmentFail({ id }),
                SharedActions.showMessage({ error: 'File download failed' }),
              ];
            }

            const resolvedFilename = this.extractFilenameFromContentDisposition(
              response.headers.get('content-disposition'),
              filename,
            );
            const url = window.URL.createObjectURL(response.body);
            const link = document.createElement('a');
            link.href = url;
            link.download = resolvedFilename;
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            return [CaseAttachmentActions.downloadAttachmentSuccess({ id })];
          }),
          catchError((error) => [
            CaseAttachmentActions.downloadAttachmentFail({ id }),
            SharedActions.showMessage({ error: this.utilService.errMessage(error) }),
          ]),
        ),
      ),
      share(),
    ),
  );

  private extractFilenameFromContentDisposition(
    contentDisposition: string | null,
    fallbackFilename: string,
  ): string {
    if (!contentDisposition) {
      return fallbackFilename;
    }

    const utfFilenameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utfFilenameMatch?.[1]) {
      try {
        return decodeURIComponent(utfFilenameMatch[1].replace(/['"]/g, ''));
      } catch {
        return fallbackFilename;
      }
    }

    const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    return filenameMatch?.[1] ?? fallbackFilename;
  }
}
